import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ConfirmModal from "../../components/admin/ConfirmModal";
import {
  assignOrder,
  cancelOrder,
  fetchAssignmentOptions,
  fetchOrderById,
  fetchOrderTimeline,
} from "../../services/orderApi";
import { fetchUserById } from "../../services/userApi";
import "../../styles/admin-common.css";

const ASSIGNABLE_STATUSES = ["PENDING", "ASSIGNED", "BUYER_ON_THE_WAY"];

const formatCurrency = (value) =>
  `₹${Number(value ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const formatRate = (rate) => {
  const pct = Number(rate ?? 0) * 100;
  return `${pct % 1 === 0 ? pct.toFixed(0) : pct.toFixed(2)}%`;
};

const OrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [assignmentOptions, setAssignmentOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [selectedBuyerId, setSelectedBuyerId] = useState("");
  const [showAssignConfirm, setShowAssignConfirm] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const loadOrder = () => {
    setLoading(true);
    setError("");
    Promise.all([
      fetchOrderById(orderId),
      fetchOrderTimeline(orderId).catch(() => null),
    ])
      .then(([orderData, timelineData]) => {
        setOrder(orderData);
        setTimeline(timelineData);
        setVendor(null);
        if (orderData.buyerId) {
          fetchUserById(orderData.buyerId)
            .then(setVendor)
            .catch(() => setVendor(null));
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadOrder(); }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const timelineEvents = useMemo(() => {
    const events = timeline?.events || [];
    return [...events].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
  }, [timeline]);

  const startAssign = async () => {
    setAssigning(true);
    setSelectedBuyerId("");
    setError("");
    try {
      const data = await fetchAssignmentOptions(orderId);
      setAssignmentOptions(data);
    } catch (err) {
      setError(err.message);
      setAssigning(false);
    }
  };

  const confirmAssign = async () => {
    setActionLoading(true);
    setError("");
    try {
      const updated = await assignOrder(orderId, selectedBuyerId);
      setOrder(updated);
      setShowAssignConfirm(false);
      setAssigning(false);
      setAssignmentOptions(null);
      if (updated.buyerId) {
        fetchUserById(updated.buyerId).then(setVendor).catch(() => setVendor(null));
      }
      fetchOrderTimeline(orderId).then(setTimeline).catch(() => {});
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const confirmCancel = async () => {
    if (!cancelReason.trim()) return;
    setActionLoading(true);
    try {
      const updated = await cancelOrder(orderId, cancelReason.trim());
      setOrder(updated);
      setShowCancelModal(false);
      setCancelReason("");
      fetchOrderTimeline(orderId).then(setTimeline).catch(() => {});
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <div className="admin-page admin-loading">Loading order</div>;

  if (error && !order) {
    return (
      <div className="admin-page">
        <p className="error-msg">{error}</p>
        <button type="button" className="admin-btn secondary" onClick={() => navigate("/orders")}>Back to orders</button>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="admin-page">
        <p>Order not found.</p>
        <button type="button" className="admin-btn secondary" onClick={() => navigate("/orders")}>Back</button>
      </div>
    );
  }

  const canAssign = ASSIGNABLE_STATUSES.includes(order.status);
  const vendors = assignmentOptions?.vendors || [];
  const selectedBuyer = vendors.find((b) => String(b.id) === String(selectedBuyerId));
  const fmt = (d) => (d ? new Date(d).toLocaleString() : "-");

  return (
    <div className="admin-page">
      <button type="button" className="admin-btn secondary" style={{ marginBottom: 16 }} onClick={() => navigate("/orders")}>
        ← Back to orders
      </button>

      <h2>Order {order.orderNumber}</h2>
      <p className="subtitle">
        <span className={`status-badge status-${order.status}`}>{order.status?.replace(/_/g, " ")}</span>
        {order.trackingId && <> · Tracking: <strong>{order.trackingId}</strong></>}
        {order.regionName && <> · Region: <strong>{order.regionName}</strong></>}
      </p>

      {error && <p className="error-msg">{error}</p>}

      <div className="details-grid">
        <div className="admin-card details-section">
          <h3>Pricing & schedule</h3>
          <p><strong>Quoted:</strong> {formatCurrency(order.quotedPrice)}</p>
          <p><strong>Final:</strong> {order.finalPrice != null ? formatCurrency(order.finalPrice) : "-"}</p>
          {order.commissionRate != null && (
            <p><strong>Commission rate:</strong> {formatRate(order.commissionRate)}</p>
          )}
          <p><strong>Pickup:</strong> {fmt(order.pickupDate)}</p>
          <p><strong>Time slot:</strong> {order.pickupTimeSlot || "-"}</p>
          <p><strong>Service pincode:</strong> {order.servicePincode || "-"}</p>
          <p><strong>Created:</strong> {fmt(order.createdAt)}</p>
          <p><strong>Updated:</strong> {fmt(order.updatedAt)}</p>
          {order.cancellationReason && (
            <p><strong>Cancel reason:</strong> {order.cancellationReason}</p>
          )}
        </div>

        <div className="admin-card details-section">
          <h3>Device</h3>
          <p><strong>{order.brandName} {order.deviceModelName}</strong></p>
          {order.device?.imageUrl && (
            <img className="device-image" src={order.device.imageUrl} alt={order.deviceModelName} />
          )}
        </div>

        {order.orderUserResponse && (
          <div className="admin-card details-section">
            <h3>Customer</h3>
            <p><strong>{order.orderUserResponse.name}</strong></p>
            <p>{order.orderUserResponse.email}</p>
            <p>{order.orderUserResponse.phoneNumber}</p>
            {order.orderUserResponse.upiId && <p>UPI: {order.orderUserResponse.upiId}</p>}
            {order.userId && (
              <Link className="admin-btn link" to={`/users/${order.userId}`}>View customer profile →</Link>
            )}
          </div>
        )}

        <div className="admin-card details-section">
          <h3>Vendor</h3>
          {order.buyerId ? (
            vendor ? (
              <>
                <p><strong>{vendor.name || vendor.email}</strong></p>
                <p>{vendor.phoneNumber || "-"}</p>
                {vendor.email && <p>{vendor.email}</p>}
                <Link className="admin-btn link" to={`/users/${order.buyerId}`}>View vendor profile →</Link>
              </>
            ) : (
              <p>Vendor ID {order.buyerId} · <Link to={`/users/${order.buyerId}`}>Open profile</Link></p>
            )
          ) : (
            <p className="muted-text">Not assigned yet</p>
          )}
        </div>

        {order.pickupAddress && (
          <div className="admin-card details-section">
            <h3>Pickup address</h3>
            <p>{order.pickupAddress.street}</p>
            <p>{order.pickupAddress.city}, {order.pickupAddress.state} {order.pickupAddress.pincode}</p>
            {order.pickupAddress.landmark && <p>Landmark: {order.pickupAddress.landmark}</p>}
          </div>
        )}
      </div>

      <div className="admin-card">
        <h3>Status timeline</h3>
        {timelineEvents.length === 0 ? (
          <p className="empty-msg">No status history recorded yet.</p>
        ) : (
          <>
            {timeline?.totalDuration != null && (
              <p className="field-hint" style={{ marginTop: 0 }}>
                Total elapsed: {timeline.totalDuration} min
                {timeline.averageStepDuration
                  ? ` · Avg step: ${Number(timeline.averageStepDuration).toFixed(0)} min`
                  : ""}
              </p>
            )}
            <ol className="order-timeline">
              {timelineEvents.map((event) => (
                <li key={event.id || `${event.status}-${event.timestamp}`} className="order-timeline-item">
                  <div className="order-timeline-marker" />
                  <div className="order-timeline-body">
                    <strong className={`status-badge status-${event.status}`}>
                      {String(event.status || "").replace(/_/g, " ")}
                    </strong>
                    <span className="order-timeline-time">{fmt(event.timestamp)}</span>
                    {event.updatedBy && (
                      <span className="muted-text"> · {event.updatedBy}</span>
                    )}
                    {event.note && <p className="order-timeline-note">{event.note}</p>}
                  </div>
                </li>
              ))}
            </ol>
          </>
        )}
      </div>

      <div className="admin-split">
        <div className="admin-card">
          <h3>Reported defects</h3>
          {!order.defects?.length ? <p className="empty-msg">None</p> : (
            <ul className="defect-detail-list">
              {order.defects.map((def) => (
                <li key={def.id}>
                  {def.icon && (
                    <img src={def.icon} alt="" className="defect-thumb" />
                  )}
                  <div>
                    <strong>{def.title}</strong>
                    {def.category && <span className="muted-text"> · {def.category}</span>}
                    {def.priceImpactApplied != null && (
                      <span className="muted-text"> · Impact {def.priceImpactApplied}%</span>
                    )}
                    {def.description && <p className="muted-text">{def.description}</p>}
                    {def.isAdditional && <em> (added at inspection)</em>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="admin-card">
          <h3>Assessment answers</h3>
          {!order.questionResponse?.length ? <p className="empty-msg">None</p> : (
            <ul>
              {order.questionResponse.map((q, idx) => {
                const labels = q.optionLabels || {};
                const formatAns = (val) => {
                  if (val == null || val === "") return "-";
                  if (typeof val === "string" && (val === "true" || val === "false")) {
                    return val === "true" ? "Yes" : "No";
                  }
                  return labels[val] || val;
                };
                const answers = q.selectedAnswers?.length
                  ? q.selectedAnswers.map(formatAns).join(", ")
                  : formatAns(q.selectedAnswer);
                return (
                  <li key={q.id || idx}>
                    <strong>{q.question}</strong>
                    <br />
                    {answers}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {canAssign && (
        <div className="admin-card">
          <h3>Vendor assignment</h3>
          <p className="subtitle">
            Vendors are listed with eligibility reasons. Assignment holds platform commission
            plus convenience fee (convenience fee is a % of commission, not of the device price)
            from the vendor wallet.
          </p>
          {!assigning ? (
            <button type="button" className="admin-btn" onClick={startAssign}>
              {order.buyerId ? "Reassign vendor" : "Assign vendor"}
            </button>
          ) : (
            <>
              {assignmentOptions && (
                <div className="assignment-summary">
                  <p>
                    <strong>Order amount:</strong> {formatCurrency(assignmentOptions.orderAmount)}
                    {" · "}
                    <strong>Commission ({formatRate(assignmentOptions.commissionRate)}):</strong>{" "}
                    {formatCurrency(assignmentOptions.commissionAmount ?? assignmentOptions.requiredCommission)}
                    {" · "}
                    <strong>Convenience fee ({formatRate(assignmentOptions.convenienceFeePercent)} of commission):</strong>{" "}
                    {formatCurrency(assignmentOptions.convenienceFeeAmount ?? 0)}
                    {" · "}
                    <strong>Total hold:</strong> {formatCurrency(assignmentOptions.requiredCommission)}
                  </p>
                </div>
              )}
              <div className="table-scroll">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Vendor</th>
                      <th>Distance</th>
                      <th>Available</th>
                      <th>On hold</th>
                      <th>Can assign</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {vendors.length === 0 && (
                      <tr><td colSpan={6} className="empty-msg">No matching vendors for this order</td></tr>
                    )}
                    {vendors.map((v) => (
                      <tr key={v.id} className={!v.canAssign ? "row-muted" : ""}>
                        <td>
                          <strong>{v.name || v.email}</strong>
                          <br />
                          <span className="muted-text">{v.phoneNumber || "-"}</span>
                        </td>
                        <td>
                          {v.distanceKm != null
                            ? `${Number(v.distanceKm).toFixed(1)} km`
                            : "-"}
                        </td>
                        <td>{formatCurrency(v.wallet?.availableBalance)}</td>
                        <td>{formatCurrency(v.wallet?.holdAmount)}</td>
                        <td>
                          {v.canAssign ? (
                            <span className="badge-active">Yes</span>
                          ) : (
                            <span className="badge-inactive">
                              {v.assignBlockReason || "No"}
                            </span>
                          )}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="admin-btn"
                            disabled={!v.canAssign}
                            onClick={() => {
                              setSelectedBuyerId(String(v.id));
                              setShowAssignConfirm(true);
                            }}
                          >
                            Select
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button type="button" className="admin-btn secondary" style={{ marginTop: 12 }} onClick={() => {
                setAssigning(false);
                setAssignmentOptions(null);
              }}>
                Cancel
              </button>
            </>
          )}
        </div>
      )}

      {order.status !== "COMPLETED" && order.status !== "CANCELLED" && (
        <div className="admin-card">
          <button type="button" className="admin-btn danger" onClick={() => setShowCancelModal(true)}>
            Cancel order
          </button>
        </div>
      )}

      {showAssignConfirm && selectedBuyer && (
        <ConfirmModal
          title="Assign vendor"
          message={`Assign order ${order.orderNumber} to ${selectedBuyer.name || selectedBuyer.email}? ${formatCurrency(assignmentOptions?.requiredCommission)} will be held from their wallet.`}
          confirmLabel="Assign"
          onConfirm={confirmAssign}
          onCancel={() => setShowAssignConfirm(false)}
          loading={actionLoading}
        />
      )}

      {showCancelModal && (
        <ConfirmModal
          title="Cancel order"
          message="Provide a cancellation reason. Wallet holds will be released per platform policy."
          confirmLabel="Cancel order"
          danger
          onConfirm={confirmCancel}
          onCancel={() => { setShowCancelModal(false); setCancelReason(""); }}
          loading={actionLoading}
        >
          <textarea
            placeholder="Cancellation reason (required)"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
        </ConfirmModal>
      )}
    </div>
  );
};

export default OrderDetails;
